import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  AssignCreativesPayload,
  Brand,
  BrandCreateFormData,
  BrandFilters,
  BrandResult,
  UpdateBrandPayload,
} from "../../schemas/brandSchema";

const BRAND_FIELDS = `
  id
  name
  username
  email
  phoneNumber
  isActive
  tenantId
  roleId
  createdBy
  createdAt
  updatedAt
  creativeCount
`;

const GET_BRANDS = gql`
  query GetBrands($page: Int, $pageSize: Int, $search: String) {
    brands(page: $page, pageSize: $pageSize, search: $search) {
      brands {
        ${BRAND_FIELDS}
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_BRAND_BY_ID = gql`
  query GetBrandById($id: ID!) {
    brand(id: $id) {
      ${BRAND_FIELDS}
    }
  }
`;

const GET_MY_BRAND_PROFILE = gql`
  query GetMyBrandProfile {
    myBrandProfile {
      ${BRAND_FIELDS}
    }
  }
`;

const CREATE_BRAND = gql`
  mutation CreateBrand($input: CreateBrandInput!) {
    createBrand(input: $input)
  }
`;

const UPDATE_BRAND = gql`
  mutation UpdateBrand($id: ID!, $input: UpdateBrandInput!) {
    updateBrand(id: $id, input: $input)
  }
`;

const DELETE_BRANDS = gql`
  mutation DeleteBrands($ids: [ID!]!) {
    deleteBrands(ids: $ids)
  }
`;

const RESET_BRAND_PASSWORD = gql`
  mutation ResetBrandPassword($id: ID!) {
    resetBrandPassword(id: $id)
  }
`;

const ASSIGN_CREATIVES_TO_BRAND = gql`
  mutation AssignCreativesToBrand($creativeIds: [ID!]!, $brandId: ID) {
    assignCreativesToBrand(creativeIds: $creativeIds, brandId: $brandId)
  }
`;

export const brandService = {
  getAll: async (filters: BrandFilters = {}): Promise<BrandResult> => {
    const { data } = await client.query({
      query: GET_BRANDS,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.brands;
  },

  getById: async (id: string): Promise<Brand> => {
    const { data } = await client.query({
      query: GET_BRAND_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.brand;
  },

  getMyProfile: async (): Promise<Brand> => {
    const { data } = await client.query({
      query: GET_MY_BRAND_PROFILE,
      fetchPolicy: "network-only",
    });
    return data.myBrandProfile;
  },

  create: async (input: BrandCreateFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_BRAND,
      variables: { input },
    });
    return data.createBrand;
  },

  update: async (payload: UpdateBrandPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_BRAND,
      variables: { id: payload.id, input: payload.brand },
    });
    return data.updateBrand;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_BRANDS,
      variables: { ids },
    });
    return data.deleteBrands;
  },

  resetPassword: async (id: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: RESET_BRAND_PASSWORD,
      variables: { id },
    });
    return data.resetBrandPassword;
  },

  assignCreatives: async (
    payload: AssignCreativesPayload,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: ASSIGN_CREATIVES_TO_BRAND,
      variables: {
        creativeIds: payload.creativeIds,
        brandId: payload.brandId,
      },
    });
    return data.assignCreativesToBrand;
  },
};
